import { enableProdMode, importProvidersFrom, ErrorHandler } from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from "@angular/material/core";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgxPermissionsModule } from "ngx-permissions";
import { CookieModule } from "ngx-cookie";
import { TagInputModule } from "ngx-chips";
import * as Sentry from "@sentry/angular";
import { Title } from "@angular/platform-browser";

import { AppComponent } from "./app/app.component";
import { environment } from "./environments/environment";
import { APP_CONFIG } from "./app/providers/app-config/app-config.types";
import { AppRoutingModule } from "./app/app-routing.module";
import { AppStoreModule } from "./app/providers/store/store.module";
import { AuthModule } from "./app/modules/auth/auth.module";
import { RegisterModule } from "./app/modules/register/register.module";
import { PageNotFoundModule } from "./app/modules/page-not-found/page-not-found.module";
import { ProfileModule } from "./app/modules/profile/profile.module";
import { AppointmentModule } from "./app/modules/appointment/appointment.module";
import { ConferenceModule } from "./app/modules/conference/conference.module";
import { ConferenceV2Module } from "./app/modules/conference-v2/conference-v2.module";
import { LayoutModule } from "./app/modules/layout/layout.module";
import { GraphqlModule } from "./app/providers/graphql/graphql.module";
import { ModalModule } from "./app/modules/modal/modal.module";
import { EmailModule } from "./app/modules/email/email.module";
import { SettingModule } from "./app/modules/setting/setting.module";
import { PreInitConditionModule } from "./app/modules/pre-init-condition/pre-init-condition.module";
import { LogAppointmentModule } from "./app/modules/logging/log-appointment.module";
import { BrandingModule } from "./app/modules/branding/branding.module";
import { PagesModule } from "./app/pages/pages.module";
import { PaymentPayOutModule } from "./app/entities/payment-pay-out/payment-pay-out.module";
import { SpecialityModule } from "./app/entities/speciality/speciality.module";
import { PaymentCountryModule } from "./app/entities/payment-country/payment-country.module";
import { PaymentInfoModule as PaymentInfoEntityModule } from "./app/entities/payment-info/payment-info.module";
import { SurveyModule } from "./app/entities/survey/survey.module";
import { MessageBusModule } from "./app/modules/message-bus/message-bus.module";
import { LegalModule } from "./app/modules/legal/legal.module";
import { LoadingSpinnerModule } from "./app/loading-spinner/loading-spinner.module";
import { UtilsModule } from "./app/modules/utils/utils.module";
import { InstitutionSettingsModule } from "./app/modules/institution-settings/institution-settings.module";
import { CustomErrorHandlerModule } from "./app/modules/custom-error-handler/custom-error-handler.module";
import { TranslateModule as CustomTranslateModule } from "./app/providers/translate/translate.module";
import { FooterLegalLinksModule } from "./app/modules/footer-legal-links/footer-legal-links.module";
import { TranslateHttpLoaderFactory } from "./app/providers/translate/factories/translate-http-loader.factory";
import { AnalyticsService } from "./app/providers/analytics/analytics.service";

if (environment.production) {
    enableProdMode();
}

const rippleConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 0,
        exitDuration: 0,
    },
};

fetch(environment.appConfigEndpoint).then(async (res) => {
    const appConfig = await res.json();
    bootstrapApplication(AppComponent, {
        providers: [
            { provide: APP_CONFIG, useValue: appConfig },
            importProvidersFrom(
                BrandingModule,
                AppStoreModule,
                AuthModule,
                NgbModule,
                NgxPermissionsModule.forRoot(),
                RegisterModule,
                PageNotFoundModule,
                ProfileModule,
                AppointmentModule,
                ConferenceModule,
                ConferenceV2Module,
                AppRoutingModule,
                LayoutModule,
                GraphqlModule,
                ModalModule,
                EmailModule,
                SettingModule,
                TranslateModule.forRoot({
                    loader: {
                        provide: TranslateLoader,
                        useFactory: TranslateHttpLoaderFactory,
                        deps: [HttpClient],
                    },
                }),
                CookieModule.forRoot(),
                TagInputModule,
                PreInitConditionModule,
                LogAppointmentModule,
                PagesModule,
                SpecialityModule,
                PaymentInfoEntityModule,
                PaymentCountryModule,
                PaymentPayOutModule,
                SurveyModule,
                MessageBusModule,
                LegalModule,
                LoadingSpinnerModule,
                UtilsModule,
                InstitutionSettingsModule,
                CustomErrorHandlerModule.forRoot(environment.production),
                CustomTranslateModule,
                FooterLegalLinksModule,
            ),
            provideAnimations(),
            provideHttpClient(withInterceptorsFromDi()),
            AnalyticsService,
            Title,
            { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: rippleConfig },
            {
                provide: ErrorHandler,
                useValue: Sentry.createErrorHandler({ showDialog: false }),
            },
        ],
    }).catch((err) => console.log(err));
});
